import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { Dialog, Switch, Transition } from "@headlessui/react";
import { getUserConfig, saveUserConfig, UserConfig } from "../../api";
import { MASTER_PROMPT_MAX_LENGTH } from "../../api/models";
import { AppStateContext } from "../../state/AppProvider";


interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
}

export const SettingsDialog = ({ open, setOpen }: Props) => {
    const cancelButtonRef = useRef(null);
    const appStateContext = useContext(AppStateContext);
    const [masterPrompt, setMasterPrompt] = useState<string>("");
    const [enabled, setEnabled] = useState<boolean>(true);
    const [maxLength, setMaxLength] = useState<number>(MASTER_PROMPT_MAX_LENGTH);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Seeding on open doubles as the Cancel reset: discarded edits are
    // replaced by the last-saved values next time the dialog opens.
    useEffect(() => {
        if (!open) return;
        setError(null);

        const applyConfig = (config: UserConfig) => {
            setMasterPrompt(config.masterPrompt);
            setEnabled(config.masterPromptEnabled);
            if (config.maxLength) setMaxLength(config.maxLength);
        };

        // Settings are preloaded into app state on app open; only fetch if
        // that preload hasn't landed yet (or failed).
        const preloaded = appStateContext?.state.userConfig;
        if (preloaded) {
            applyConfig(preloaded);
            return;
        }

        setLoading(true);
        getUserConfig()
            .then(config => {
                if (config) {
                    appStateContext?.dispatch({ type: "UPDATE_USER_CONFIG", payload: config });
                    applyConfig(config);
                } else {
                    setError("Could not load your settings. Please try again.");
                }
            })
            .finally(() => setLoading(false));
    }, [open]);

    const onSave = async () => {
        setSaving(true);
        setError(null);
        const saved = await saveUserConfig({ masterPrompt, masterPromptEnabled: enabled }).finally(() => setSaving(false));
        if (saved) {
            appStateContext?.dispatch({ type: "UPDATE_USER_CONFIG", payload: saved });
            setOpen(false);
        } else {
            setError("Could not save your preferences. Please try again.");
        }
    };

    const onClose = () => {
        setOpen(false);
        setError(null);
    };

    return (
        <Transition show={open} as={Fragment}>
            <Dialog className="relative z-[100]" initialFocus={cancelButtonRef} onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-jtc-very-dark-grey bg-opacity-50 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg rounded-md">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mt-3 sm:ml-4 sm:mt-0 text-left w-full">
                                            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                                Settings
                                            </Dialog.Title>
                                            <div className="mt-4 flex items-center justify-between">
                                                <div>
                                                    <h4 className="text-sm font-semibold text-gray-900">Personal preferences</h4>
                                                    <p className="text-sm text-gray-500">
                                                        Tell ChatJTC about yourself and how you'd like it to respond — tone, format, language.
                                                    </p>
                                                </div>
                                                <Switch
                                                    checked={enabled}
                                                    onChange={setEnabled}
                                                    className={
                                                        "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ml-4" +
                                                        (enabled ? " bg-jtc-blue" : " bg-jtc-grey")
                                                    }
                                                >
                                                    <span className="sr-only">Apply personal preferences</span>
                                                    <span
                                                        aria-hidden="true"
                                                        className={
                                                            "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out" +
                                                            (enabled ? " translate-x-5" : " translate-x-0")
                                                        }
                                                    />
                                                </Switch>
                                            </div>
                                            <textarea
                                                className="text-sm mt-3 w-full resize-none bg-transparent focus-visible:outline-0 focus:ring-0 focus-visible:ring-0 p-2 h-[200px] border placeholder-jtc-very-dark-grey border-jtc-grey focus:border-jtc-grey focus-visible:border-jtc-grey disabled:opacity-50"
                                                placeholder="Tell us about yourself and how you'd like assistants to respond..."
                                                rows={1}
                                                maxLength={maxLength}
                                                value={masterPrompt}
                                                disabled={loading}
                                                onChange={ev => setMasterPrompt(ev.target.value)}
                                            ></textarea>
                                            <div className="flex justify-between items-center mt-1">
                                                <span className="text-sm text-jtc-pink">{error}</span>
                                                <span className={"text-xs" + (masterPrompt.length >= maxLength ? " text-jtc-pink" : " text-gray-500")}>
                                                    {masterPrompt.length}/{maxLength}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="px-4 pb-6 flex flex-row sm:flex-row-reverse sm:px-6 gap-4">
                                    <button className="jtc-secondary-button m-0 w-[115px]" onClick={onClose} ref={cancelButtonRef}>
                                        <span>
                                            <span>Cancel</span>
                                        </span>
                                    </button>
                                    <button
                                        className="jtc-primary-button m-0 w-[115px] disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={onSave}
                                        disabled={loading || saving}
                                    >
                                        <span>
                                            <span>{saving ? "Saving..." : "Save"}</span>
                                        </span>
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
